// data/strategic-playbook/pulse.js
// ════════════════════════════════════════════════════
// STRATEGIC PLAYBOOK — Pulse / dashboard data
// ════════════════════════════════════════════════════
// Hardcoded for v1.0. v1.1 generates this weekly via cron.
// ════════════════════════════════════════════════════

export const PULSE = {
  weekOf: 'May 3, 2026',
  hero: {
    eyebrow: 'This Week\'s Strategic Pulse',
    headline:
      'Indiana 16-0 + national title flips the asymmetric-bet thesis from "year-2 caveat" to "doubly proved."',
    subline:
      'Confidence on Pillar 2 dossiers (Talent & Acquisition) rises 65 → 88. Worth re-reading if you\'re hiring, signing a co-founder, or making any non-pedigree bet.',
    meta: ['7 situations tracked', '12 hypotheses under test', '3 dossiers updated'],
  },
};

export const TRACKED_SITUATIONS = [
  {
    id: 'celtics-2024-defense',
    name: 'Boston Celtics — Title-Defense Year 2',
    stage: 'warning', // deviation | warning | window | post
    stageLabel: 'Warning Signals Visible',
    shift:
      'Roster math from the 2024 title now fully due — Tatum extension active, Brown deal year 2, age curve testing chemistry.',
    pillars: [7, 2],
    confidence: 71,
  },
  {
    id: 'openai-leadership-churn',
    name: 'OpenAI — Mission vs Product Divergence',
    stage: 'warning',
    stageLabel: 'Warning Signals Visible',
    shift:
      'Continued safety-org churn. Each departure is a public referendum on a different version of the mission.',
    pillars: [1, 7],
    confidence: 64,
  },
  {
    id: 'harbaugh-chargers-y2',
    name: 'Jim Harbaugh / Chargers — College-to-NFL Year 2',
    stage: 'window',
    stageLabel: 'Intervention Window Open',
    shift:
      'Year 2 of NFL rebuild post-Michigan title. Question: does college culture import or get diluted? Pillar 7 + 4.',
    pillars: [7, 4],
    confidence: 58,
  },
  {
    id: 'klarna-ai-restructuring',
    name: 'Klarna — AI-First Workforce Restructuring',
    stage: 'window',
    stageLabel: 'Intervention Window Open',
    shift:
      'Highest-visibility test of "AI replaces functions." Customer relationship survival is the open question.',
    pillars: [4, 2],
    confidence: 52,
  },
  {
    id: 'stripe-ipo-delay',
    name: 'Stripe — Multi-Year IPO Delay',
    stage: 'deviation',
    stageLabel: 'Deviation in Progress',
    shift:
      'Tender offers again instead of going public. The canonical "when does staying small cost more than going big?"',
    pillars: [5, 1],
    confidence: 67,
  },
];

export const HYPOTHESES = [
  {
    id: 'H1',
    text: 'Champions who pay all stars max contracts decline within 3 years 70% of the time.',
    activeTests: 'Celtics (year 2), Warriors reference',
    score: '7 of 10 historical support',
    progress: 70,
  },
  {
    id: 'H3',
    text: 'College-to-NFL coaches lose 30%+ of cultural intensity within 24 months.',
    activeTests: 'Harbaugh / Chargers (year 2), Carroll/Saban references',
    score: '8 of 12 historical support',
    progress: 67,
  },
  {
    id: 'H4',
    text: 'AI-driven workforce reduction works in <40% of public cases without service quality drop within 18 months.',
    activeTests: 'Klarna, Duolingo, others',
    score: '4 cases active, none at 18mo yet',
    progress: 33,
  },
  {
    id: 'H7',
    text: 'Asymmetric-bet leadership hires (multi-level track record + system) succeed at 3x rate of pedigree hires after year 2.',
    activeTests: 'Indiana football (validated y2), others queued',
    score: 'Newly elevated to 88 confidence',
    progress: 88,
  },
];

export const CONFIDENCE_MOVEMENT = [
  { name: 'Indiana — Asymmetric Bet', delta: +23, direction: 'up' },
  { name: 'Pandemic Playbook', delta: +5, direction: 'up' },
  { name: 'OpenAI — Mission', delta: +3, direction: 'up' },
  { name: 'Heat — Super-Team', delta: 0, direction: 'flat' },
  { name: 'Stripe — IPO Delay', delta: -2, direction: 'down' },
  { name: 'Klarna — AI Restructuring', delta: -8, direction: 'down' },
];

// TECTONIC_SHIFTS lives in data/strategic-playbook/tectonic-shifts.js
// (Phase F12 promotion — each shift now has a deeper data shape and
// dedicated detail pages). Re-exported here so existing imports of
// PULSE-adjacent data from this file keep working without churn.
export { TECTONIC_SHIFTS } from './tectonic-shifts';

export const FEATURED_DOSSIER_ID = 'indiana-2024-asymmetric';

export const THINKER_QUOTE = {
  body:
    'I\'ve seen guys with $500 million blow up because of leverage. Leverage is the only way a smart guy can go broke. You do smart things, you eventually get rich. You do smart things with leverage and you do one wrong thing along the way, and it could wipe you out, because anything times zero is zero.',
  thinker: 'Warren Buffett',
  context: 'Linked to: Pillar 5 — Decision Architecture',
};

export const WORTH_NOTING = [
  {
    tag: 'Pattern emerging',
    text:
      'OpenAI added 5 senior leaders this week — new pattern emerging in the leadership churn dossier. Watching for whether this stabilizes the safety/product split or amplifies it.',
  },
  {
    tag: 'Cascade completing',
    text:
      'Pet adoption rates returning to pre-COVID levels — completes Pandemic Playbook Phase 6 rotation. Chewy revenue growth slowing in line with the dossier\'s prediction.',
  },
  {
    tag: 'Insider signal',
    text:
      'Stripe S-1 filings hint at IPO timeline shift to early 2027. Combined with tender offer activity, the "stay private" thesis is showing first cracks.',
  },
];

export const BLACK_SWAN_GAUGE = {
  status: 'Quiet',
  statusColor: 'forest-moss',
  detail:
    'No shock-type signals at elevated levels this week. Pandemic, banking, and supply-chain monitors all green. Tech-breakthrough monitor steady at "active" (AI continues).',
  active: '0 of 8 shock playbooks at elevated risk',
};
